import React from 'react'
import { useTranslation } from 'react-i18next'
import { useGameStore } from './useGameStore'
import { useNavigationStore } from './useNavigationStore.js'
import { useAuthStore } from './useAuthStore.js'
import { FeedbackButton } from './FeedbackButton.js'
import { S } from '../locales/strings.js'
import { GameDataService } from '../services/GameDataService.js'
import { CampaignSystem } from '../engine/CampaignSystem.js'
import campaignData from '../data/campaign.json'
import type { CampaignMissionDef } from '../interfaces/index.js'

const API_BASE = window.location.origin.includes('localhost') ? 'http://localhost:3001' : ''

async function recordMatchResult(
  accessToken: string,
  outcome: 'win' | 'draw' | 'loss',
  opponentLeaguePoints: number | null,
): Promise<void> {
  const body: Record<string, unknown> = { outcome }
  if (opponentLeaguePoints !== null) {
    body.opponentLeaguePoints = opponentLeaguePoints
  }
  await fetch(`${API_BASE}/api/leaderboard/record`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(body),
  })
}

const _campaignMissions = campaignData as CampaignMissionDef[]

const UNIT_ICON: Record<string, string> = {
  goblin: '👺', firespirit: '🔥', frostgolem: '❄️', vinewurm: '🌿',
  crossbow: '🏹', flameknight: '🛡️', icetitan: '🧊', thornbeast: '🌿',
  paladin: '⚔️', dragon: '🐉', abysslord: '👹', stormeagle: '🦅',
}
const TOWER_ICON: Record<string, string> = {
  archer: '🏹', firemage: '🔥', frost: '❄️', lightning: '⚡',
  shadow: '🌑', goldmine: '💰', wall: '🧱', mudpit: '🟫', thorns: '🌵',
}

interface MatchResultProps {
  onPlayAgain?: () => void
  onMainMenu?: () => void
}

const defaultReload = (): void => { window.location.reload() }

function formatDuration(ms: number): string {
  const totalSecs = Math.floor(ms / 1000)
  const m = Math.floor(totalSecs / 60)
  const s = totalSecs % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

interface DamageBarProps {
  label: string
  icon: string
  damage: number
  maxDamage: number
  color: string
}

function DamageBar({ label, icon, damage, maxDamage, color }: DamageBarProps): React.ReactElement {
  const pct = maxDamage > 0 ? (damage / maxDamage) * 100 : 0
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#ccc', marginBottom: 2 }}>
        <span>{icon} {label}</span>
        <span style={{ color: '#aaa' }}>{damage.toLocaleString()}</span>
      </div>
      <div style={{ height: 5, background: '#1a1430', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.4s ease' }} />
      </div>
    </div>
  )
}

interface StatColumnProps {
  label: string
  color: string
  totalDamage: number
  goldEarned: number
  unitsSent: number
  unitsSentMap: Record<string, number>
  towersBuilt: number
  towerDamage: Record<string, number>
  tLabel: (key: string) => string
}

function StatColumn({
  label, color, totalDamage, goldEarned, unitsSent, unitsSentMap,
  towersBuilt, towerDamage, tLabel,
}: StatColumnProps): React.ReactElement {
  const gds = GameDataService.getInstance()

  const sortedTowers = Object.entries(towerDamage).sort(([, a], [, b]) => b - a)
  const maxTowerDmg = sortedTowers[0]?.[1] ?? 1

  const sortedUnits = Object.entries(unitsSentMap).sort(([, a], [, b]) => b - a)

  return (
    <div style={{
      flex: 1,
      background: 'rgba(0,0,0,0.5)',
      border: `1px solid ${color}44`,
      borderRadius: 10,
      padding: '12px 14px',
      minWidth: 0,
    }}>
      <div style={{ fontWeight: 700, color, fontSize: 14, marginBottom: 10, letterSpacing: 1 }}>
        {label}
      </div>

      {/* Summary stats */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <StatPill icon="💥" value={totalDamage.toLocaleString()} />
        <StatPill icon="💰" value={`+${goldEarned.toLocaleString()}`} />
        <StatPill icon="🏗" value={String(towersBuilt)} />
        <StatPill icon="🚀" value={String(unitsSent)} />
      </div>

      {/* Tower damage breakdown */}
      {sortedTowers.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
            {tLabel(S.MATCH_RESULT_TOWER_DAMAGE)}
          </div>
          {sortedTowers.map(([towerId, dmg]) => {
            const towerName = gds.getTowerById(towerId)?.name ?? towerId
            const icon = TOWER_ICON[towerId] ?? '🗼'
            return (
              <DamageBar
                key={towerId}
                label={towerName}
                icon={icon}
                damage={dmg}
                maxDamage={maxTowerDmg}
                color={color}
              />
            )
          })}
        </div>
      )}

      {/* Units sent breakdown */}
      {sortedUnits.length > 0 && (
        <div>
          <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
            {tLabel(S.MATCH_RESULT_UNITS_BREAKDOWN)}
          </div>
          {sortedUnits.map(([unitId, count]) => {
            const unitName = gds.getUnitById(unitId)?.name ?? unitId
            const icon = UNIT_ICON[unitId] ?? '👾'
            return (
              <div key={unitId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#ccc', marginBottom: 3 }}>
                <span>{icon} {unitName}</span>
                <span style={{ color: '#3A8AE8' }}>×{count}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function StatPill({ icon, value }: { icon: string; value: string }): React.ReactElement {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 4,
      background: 'rgba(255,255,255,0.06)', borderRadius: 6,
      padding: '3px 8px', fontSize: 11, color: '#ddd',
    }}>
      <span>{icon}</span>
      <span style={{ fontFamily: 'monospace' }}>{value}</span>
    </div>
  )
}

function CampaignResultBanner({ missionIndex, outcome }: { missionIndex: number; outcome: 'win' | 'lose' | 'draw' }): React.ReactElement | null {
  const mission = _campaignMissions[missionIndex]
  if (!mission) return null

  const cs = CampaignSystem.getInstance()
  const savedProgress = cs.getSavedProgress()
  const saved = savedProgress[mission.id]
  const stars = saved?.stars ?? 0

  return (
    <div style={{
      background: outcome === 'win' ? 'rgba(74,170,58,0.12)' : 'rgba(232,64,64,0.12)',
      border: `1px solid ${outcome === 'win' ? '#4AAA3A' : '#E84040'}`,
      borderRadius: 10,
      padding: '10px 16px',
      marginBottom: 14,
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 13, color: outcome === 'win' ? '#4AAA3A' : '#E84040', fontWeight: 700, marginBottom: 4 }}>
        {outcome === 'win' ? '✓ Mission abgeschlossen' : '✗ Mission fehlgeschlagen'}
      </div>
      <div style={{ fontSize: 12, color: '#ccc', marginBottom: 6 }}>{mission.icon} {mission.titleKey}</div>
      {outcome === 'win' && (
        <div style={{ fontSize: 18 }}>
          {Array.from({ length: 3 }, (_, i) => (
            <span key={i} style={{ color: i < stars ? '#C9A84C' : '#333', marginRight: 2 }}>★</span>
          ))}
        </div>
      )}
      {outcome === 'win' && (
        <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
          +{mission.reward.xp} XP · +{mission.reward.gold} Gold
          {mission.reward.packType !== undefined && ` · Pack: ${mission.reward.packType}`}
        </div>
      )}
    </div>
  )
}

export function MatchResult({
  onPlayAgain = defaultReload,
  onMainMenu = defaultReload,
}: MatchResultProps): React.ReactElement | null {
  const matchOutcome = useGameStore((s) => s.matchOutcome)
  const winnerName = useGameStore((s) => s.winnerName)
  const localPlayerId = useGameStore((s) => s.localPlayerId)
  const matchStats = useGameStore((s) => s.matchStats)
  const opponentLeaguePoints = useGameStore((s) => s.opponentLeaguePoints)
  const gameMode = useNavigationStore((s) => s.gameMode)
  const selectedMissionIndex = useNavigationStore((s) => s.selectedMissionIndex)
  const session = useAuthStore((s) => s.session)

  const { t } = useTranslation()

  React.useEffect(() => {
    if (matchOutcome === null) return
    if (session === null) return
    if (gameMode !== 'pvp') return
    const outcome: 'win' | 'draw' | 'loss' =
      matchOutcome === 'win' ? 'win' : matchOutcome === 'draw' ? 'draw' : 'loss'
    recordMatchResult(session.token.accessToken, outcome, opponentLeaguePoints).catch(() => {})
  // Only fire once when the component mounts with a valid outcome
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (matchOutcome === null) return null

  let outcomeText: string
  let outcomeColor: string
  if (matchOutcome === 'win') {
    outcomeText = t(S.MATCH_RESULT_VICTORY)
    outcomeColor = '#00ff88'
  } else if (matchOutcome === 'lose') {
    outcomeText = t(S.MATCH_RESULT_DEFEAT)
    outcomeColor = '#ff4444'
  } else {
    outcomeText = t(S.MATCH_RESULT_DRAW)
    outcomeColor = '#ffee00'
  }

  const winnerDisplay =
    winnerName !== null && winnerName !== localPlayerId ? winnerName : null

  const hasStats = matchStats !== null
  const matchDuration = matchStats?.matchDurationMs ?? null

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      background: 'rgba(0,0,0,0.88)',
      zIndex: 100,
      pointerEvents: 'auto',
      fontFamily: 'monospace',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      overflowY: 'auto',
      padding: '24px 16px',
      boxSizing: 'border-box',
    }}>
      {/* Title */}
      <div style={{
        fontSize: 42, fontWeight: 900, color: outcomeColor,
        textShadow: `0 0 32px ${outcomeColor}`,
        marginBottom: 4,
        letterSpacing: 2,
      }}>
        {outcomeText}
      </div>

      {winnerDisplay !== null && (
        <div style={{ color: '#aaa', fontSize: 14, marginBottom: 8 }}>
          {t(S.MATCH_RESULT_WINNER)} {winnerDisplay}
        </div>
      )}

      {matchDuration !== null && (
        <div style={{ color: '#888', fontSize: 12, marginBottom: 12 }}>
          {t(S.MATCH_RESULT_DURATION)} {formatDuration(matchDuration)}
        </div>
      )}

      {/* Campaign banner */}
      {gameMode === 'campaign' && selectedMissionIndex !== null && (
        <div style={{ width: '100%', maxWidth: 640 }}>
          <CampaignResultBanner missionIndex={selectedMissionIndex} outcome={matchOutcome} />
        </div>
      )}

      {/* Per-player stats */}
      {!hasStats ? (
        <div style={{ color: '#666', fontStyle: 'italic', fontSize: 14, marginTop: 8 }}>
          {t(S.MATCH_RESULT_NO_STATS)}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 12, width: '100%', maxWidth: 640, alignItems: 'flex-start' }}>
          <StatColumn
            label={t(S.MATCH_RESULT_PLAYER)}
            color="#88aaff"
            totalDamage={matchStats.player.totalDamage}
            goldEarned={matchStats.player.goldEarned}
            unitsSent={matchStats.player.unitsSent}
            unitsSentMap={matchStats.player.unitsSentMap}
            towersBuilt={matchStats.player.towersBuilt}
            towerDamage={matchStats.player.towerDamage}
            tLabel={t}
          />
          <StatColumn
            label={t(S.MATCH_RESULT_OPPONENT)}
            color="#ff8888"
            totalDamage={matchStats.opponent.totalDamage}
            goldEarned={matchStats.opponent.goldEarned}
            unitsSent={matchStats.opponent.unitsSent}
            unitsSentMap={matchStats.opponent.unitsSentMap}
            towersBuilt={matchStats.opponent.towersBuilt}
            towerDamage={matchStats.opponent.towerDamage}
            tLabel={t}
          />
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button
          style={btnStyle}
          onClick={onPlayAgain}
        >
          {t(S.MATCH_RESULT_PLAY_AGAIN)}
        </button>
        <button
          style={{ ...btnStyle, background: 'rgba(155,111,232,0.15)', borderColor: '#9B6FE8', color: '#C9A84C' }}
          onClick={onMainMenu}
        >
          {t(S.MATCH_RESULT_MAIN_MENU)}
        </button>
      </div>

      <FeedbackButton style={{ marginTop: 12 }} />
    </div>
  )
}

const btnStyle: React.CSSProperties = {
  padding: '12px 28px',
  fontSize: 15,
  fontFamily: 'monospace',
  background: 'rgba(20,16,32,0.9)',
  color: '#ffffff',
  border: '2px solid #3a3050',
  borderRadius: 8,
  cursor: 'pointer',
  minHeight: 44,
  minWidth: 140,
}
